import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingBag, Check } from "lucide-react";
import { apiGet } from "../lib/api";

const TONE = { blue: "#243B52", ochre: "#CF9026", oxblood: "#4E0E18" };

// GTA-style radial merch wheel. Drag anywhere with a thumb/finger to spin the
// selector; release near an item to lock it, then tap the hub to add to cart.
export default function MerchWheelDemo() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [added, setAdded] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [dragging, setDragging] = useState(false);
  const wheelRef = useRef(null);

  useEffect(() => {
    apiGet("/merch").then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const n = items.length;
  const step = n ? 360 / n : 0;

  // place item i at angle: -90 (top) + i*step
  const positions = useMemo(
    () => items.map((_, i) => -90 + i * step),
    [items, step]
  );

  const pickFromPointer = (clientX, clientY) => {
    const el = wheelRef.current;
    if (!el || !n) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const deg = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI; // -180..180
    let best = 0;
    let bestDiff = 999;
    positions.forEach((p, i) => {
      let d = Math.abs(((deg - p + 540) % 360) - 180);
      if (d < bestDiff) { bestDiff = d; best = i; }
    });
    setActive(best);
  };

  const onDown = (e) => {
    setDragging(true);
    const t = e.touches ? e.touches[0] : e;
    pickFromPointer(t.clientX, t.clientY);
  };
  const onMove = (e) => {
    if (!dragging) return;
    const t = e.touches ? e.touches[0] : e;
    pickFromPointer(t.clientX, t.clientY);
  };
  const onUp = () => setDragging(false);

  const addToCart = () => {
    const item = items[active];
    if (!item) return;
    setCartCount((c) => c + 1);
    setAdded(item.id);
    setTimeout(() => setAdded(null), 900);
  };

  const activeItem = items[active];
  const indicatorDeg = positions[active] ?? -90;

  return (
    <div
      className="fixed inset-0 z-[100] grain overflow-hidden select-none"
      style={{ background: "radial-gradient(circle at 50% 45%, #241a16, #15100E 70%)" }}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {/* header */}
      <div className="absolute top-0 inset-x-0 h-16 flex items-center justify-between px-6 text-[#ECE7DB] z-20">
        <span className="font-tech text-[10px] tracking-[0.3em] text-[#CF9026]">PROTOTYPE · MERCH WHEEL</span>
        <span data-testid="wheel-cart" className="font-tech text-xs tracking-[0.2em] flex items-center gap-2">
          <ShoppingBag size={15} /> {cartCount}
        </span>
      </div>

      <p className="absolute top-14 inset-x-0 text-center font-tech text-[#ECE7DB]/50 text-[11px] tracking-[0.25em] z-20">
        DRAG WITH YOUR THUMB · RELEASE ON AN ITEM · TAP CENTER TO ADD
      </p>

      {/* WHEEL */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          ref={wheelRef}
          data-testid="merch-wheel"
          onMouseDown={onDown}
          onTouchStart={onDown}
          className="relative touch-none"
          style={{ width: "min(92vw, 620px)", height: "min(92vw, 620px)", cursor: dragging ? "grabbing" : "grab" }}
        >
          {/* rotating slice highlight */}
          <div
            className="absolute inset-0 rounded-full transition-transform duration-200 ease-out"
            style={{
              transform: `rotate(${indicatorDeg + 90}deg)`,
              background: `conic-gradient(from -${step / 2}deg, rgba(207,144,38,0.28) 0deg, rgba(207,144,38,0.28) ${step}deg, transparent ${step}deg)`,
              WebkitMaskImage: "radial-gradient(circle, transparent 33%, #000 33.5%, #000 100%)",
              maskImage: "radial-gradient(circle, transparent 33%, #000 33.5%, #000 100%)",
            }}
          />
          {/* outer + inner rings */}
          <div className="absolute inset-0 rounded-full border-2 border-[#ECE7DB]/15" />
          <div className="absolute rounded-full border-2 border-[#ECE7DB]/10" style={{ inset: "16%" }} />

          {/* pointer needle */}
          <div
            className="absolute left-1/2 top-1/2 origin-left h-[2px] transition-transform duration-200 ease-out"
            style={{
              width: "50%",
              transform: `translateY(-50%) rotate(${indicatorDeg}deg)`,
              background: "linear-gradient(90deg, transparent, #CF9026)",
            }}
          />

          {/* items */}
          {items.map((it, i) => {
            const ang = (positions[i] * Math.PI) / 180;
            const R = 41; // % from center
            const x = 50 + R * Math.cos(ang);
            const y = 50 + R * Math.sin(ang);
            const isActive = i === active;
            return (
              <button
                key={it.id}
                data-testid={`wheel-item-${it.id}`}
                onClick={(e) => { e.stopPropagation(); setActive(i); }}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden border-2 transition-all duration-200"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: isActive ? 92 : 62,
                  height: isActive ? 92 : 62,
                  borderColor: isActive ? "#CF9026" : "rgba(236,231,219,0.25)",
                  boxShadow: isActive ? "0 0 0 4px rgba(207,144,38,0.25), 0 10px 30px rgba(0,0,0,0.5)" : "none",
                  background: TONE[it.tone] || "#4E0E18",
                  zIndex: isActive ? 10 : 5,
                }}
              >
                <img src={it.img} alt={it.name} className="w-full h-full object-cover" />
              </button>
            );
          })}

          {/* CENTER HUB */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center text-center px-6"
            style={{ width: "33%", height: "33%", background: "#15100E", border: "2px solid rgba(236,231,219,0.2)" }}
          >
            {activeItem ? (
              <>
                <p className="font-display font-extrabold stretch-text text-[#ECE7DB] leading-[0.9]" style={{ fontSize: "clamp(13px,2.2vw,20px)" }}>
                  {activeItem.name}
                </p>
                <p className="font-tech text-[#CF9026] text-sm mt-1">
                  {typeof activeItem.price === "number" ? `$${activeItem.price}` : activeItem.price}
                </p>
                <button
                  data-testid="wheel-add"
                  onClick={(e) => { e.stopPropagation(); addToCart(); }}
                  className="mt-3 font-tech text-[10px] tracking-[0.2em] px-4 py-2 flex items-center gap-2 transition-colors"
                  style={{ background: added === activeItem.id ? "#CF9026" : "#ECE7DB", color: "#15100E" }}
                >
                  {added === activeItem.id ? (<><Check size={13} /> ADDED</>) : (<><ShoppingBag size={13} /> ADD TO CART</>)}
                </button>
              </>
            ) : (
              <p className="font-tech text-[#ECE7DB]/50 text-xs tracking-widest">LOADING…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
